import { AppRoutes } from '../common/constants';
import { Box, Button } from '@mui/material';
import { ClientType } from '../common/types/client';
import { formatAmount } from '../common/components/dataGrid/dataGridEditNumberCell';
import { GridColDef } from '@mui/x-data-grid';
import { JSX } from 'react';
import { makeSignedS3Url } from '../common/helpers/makeSignedS3Url';
import { privateFulcrumApi } from '../common/lib/fulcrumApi';
import { QuoteItem, QuoteStatus } from '../common/types/quotes';
import { useNavigate } from 'react-router-dom';
import DataGrid, { DataGridProps } from '../common/components/dataGrid';
import DataGridLoadingState from '../common/components/dataGrid/dataGridLoadingState';
import QuoteStatusCell from './cellRenderers/quoteStatusCell';
import RequestQuoteButton from './requestQuoteButton';

interface QuotesProps extends Omit<DataGridProps<QuoteItem>, 'columns' | 'rows'> {
  quotes?: QuoteItem[];
}

/**
 * Render quotes table
 */
const Quotes = ({ quotes, ...gridProps }: QuotesProps): JSX.Element => {
  const navigate = useNavigate();

  const handleClickFix = (quote: QuoteItem): void => {
    navigate(
      `${
        quote.partyRoleplayerTypeID === ClientType.Business
          ? AppRoutes.LegalEntities
          : AppRoutes.NaturalPersons
      }/${quote.partyRoleplayerID}/edit`,
    );
  };

  const handleClickEdit = (quote: QuoteItem): void => {
    privateFulcrumApi.get(`/FNAQuoteFlyover/${quote.id}`).then(({ data }) => {
      window.location.href = data.myriadUrl;
    });
  };

  const columns: GridColDef<QuoteItem>[] = [
    {
      field: 'policyholder',
      headerName: 'Policy holder',
      flex: 2,
    },
    {
      field: 'lifeinsured',
      headerName: 'Life insured',
      flex: 2,
    },
    {
      field: 'deathBenefit',
      headerName: 'Death benefit',
      cellClassName: 'whitespace-nowrap',
      flex: 1,
      valueFormatter: (value) => (value ? formatAmount(value) : '-'),
    },
    {
      field: 'quotePremium',
      headerName: 'Premium',
      cellClassName: 'whitespace-nowrap',
      flex: 1,
      valueFormatter: (value) => (value ? formatAmount(value) : '-'),
    },
    {
      field: 'quoteDiscountedPremium',
      headerName: 'Discounted premium',
      cellClassName: 'whitespace-nowrap',
      flex: 1,
      valueFormatter: (value) => (value ? formatAmount(value) : '-'),
    },
    {
      field: 'quotePolicyFee',
      headerName: 'Policy fee',
      cellClassName: 'whitespace-nowrap',
      flex: 1,
      valueFormatter: (value) => (value ? formatAmount(value) : '-'),
    },
    {
      field: 'statusID',
      headerName: 'Quote status',
      flex: 1,
      renderCell: QuoteStatusCell,
    },
    {
      field: 'action',
      width: 120,
      headerName: '',
      renderCell: ({ row }) => (
        <>
          {row.statusID === QuoteStatus.CanQuote && (
            <RequestQuoteButton quote={row} variant="contained" color="secondary">
              Quote
            </RequestQuoteButton>
          )}
          {row.statusID === QuoteStatus.Invalid && (
            <Button onClick={(): void => handleClickFix(row)} variant="outlined">
              Fix
            </Button>
          )}
          {row.statusID === QuoteStatus.Failed && (
            <RequestQuoteButton quote={row} variant="outlined">
              Retry
            </RequestQuoteButton>
          )}
          {row.statusID === QuoteStatus.Quoted && (
            <Box className="flex flex-col items-start space-y-2">
              <Button
                size="small"
                component="a"
                href={row.quotePdfUrl && makeSignedS3Url(row.quotePdfUrl)}
                target="_blank"
                variant="contained"
                color="success"
              >
                View PDF
              </Button>
              <Button size="small" onClick={(): void => handleClickEdit(row)}>
                Open in Myriad
              </Button>
            </Box>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      {quotes ? (
        <DataGrid {...gridProps} rows={quotes} columns={columns} />
      ) : (
        <DataGridLoadingState />
      )}
    </>
  );
};
export default Quotes;
